import { FC } from 'react';
import { upperFirst } from 'lodash';

import { Box } from '@qwealth/qcore';

import TruncateTextByTextHeight from 'components/common/TruncateTextByTextHeight';
import { Kyp } from 'data/models/Kyp';
import KypDocumentButton from '../KypDocument/KypDocumentButton';
import { usePartnerMap } from 'hooks/usePartnerMap';
import { S3Item } from '@qwealth/qvault';
import { format } from 'date-fns';

interface KypDetailsTableProps {
  kyp: Kyp;
  attachments: S3Item[];
  clickAttachment: (url: string, fileName: string, type: string) => void;
}

const KypDetailsTable: FC<KypDetailsTableProps> = ({ kyp, attachments, clickAttachment }): JSX.Element => {
  const partnerMap = usePartnerMap();
  const hasAttachments = attachments.length !== 0;

  return (
    <Box>
      <Box fontWeight="bold" fontSize="large" marginTop="large">
        Product
      </Box>
      <Box marginLeft="largest">
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <tbody>
            <tr>
              <Box as="td" fontWeight="bold" width="20vh">
                Name
              </Box>
              <td>{kyp.name}</td>
            </tr>
            <tr>
              <Box as="td" fontWeight="bold" width="20vh">
                Type
              </Box>
              <td>{kyp.type}</td>
            </tr>
            <tr>
              <Box as="td" fontWeight="bold" width="20vh">
                Asset Class
              </Box>
              <td>{kyp.assetClass}</td>
            </tr>
            <tr>
              <Box as="td" fontWeight="bold" width="20vh">
                Ticker
              </Box>
              <td>{kyp.ticker}</td>
            </tr>
            <tr>
              <Box as="td" fontWeight="bold" width="20vh">
                ISM Code
              </Box>
              <td>{kyp.isinCode}</td>
            </tr>
            <tr>
              <Box as="td" fontWeight="bold" width="20vh">
                Partner
              </Box>
              <td>{partnerMap[kyp.partner]?.partnerName}</td>
            </tr>
          </tbody>
        </table>
      </Box>

      <Box fontWeight="bold" fontSize="large">
        Attachments
      </Box>
      <Box marginLeft="largest" marginTop="default" display="flex" flexDirection="column" gap="default" marginBottom="small">
        {hasAttachments
          ? attachments.map((attachment, index) => {
              return <KypDocumentButton key={'kypDocumentButton' + index} attachment={attachment} clickAttachment={clickAttachment} />;
            })
          : 'No Attachments'}
      </Box>

      <Box fontWeight="bold" fontSize="large">
        Assessment
      </Box>
      <Box marginLeft="largest">
        <TruncateTextByTextHeight text={kyp.assessment} />
      </Box>

      <Box fontWeight="bold" fontSize="large" marginTop="default">
        Compliance
      </Box>
      <Box marginLeft="largest">
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <tbody>
            <tr>
              <Box as="td" fontWeight="bold" width="20vh">
                Reviewed By
              </Box>
              <td>{kyp.reviewedBy ? kyp.reviewedBy : '-'}</td>
            </tr>
            <tr>
              <Box as="td" fontWeight="bold" width="20vh">
                Review Date
              </Box>
              <td>{kyp.reviewDate ? format(new Date(kyp.reviewDate), 'MMMM d, yyyy') : '-'}</td>
            </tr>
            <tr>
              <Box as="td" fontWeight="bold" width="20vh">
                Approval Status
              </Box>
              <td>{upperFirst(kyp.reviewStatus)}</td>
            </tr>
            <tr>
              <Box as="td" fontWeight="bold" width="20vh">
                Approved For
              </Box>
              <td>{kyp.reviewStatus === 'approved' || kyp.reviewStatus === 'expired' ? kyp.approvedFor : '-'}</td>
            </tr>
          </tbody>
        </table>
      </Box>
    </Box>
  );
};

export default KypDetailsTable;
